import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Accept, useDropzone } from 'react-dropzone';
import {
  AbsenceFilesPlaceholderIcon,
  AppButton,
  Box,
  CalendarIcon,
  capitalizeFirstLetter,
  ClearAll,
  CONTENT_ID_SELECTOR,
  DateRangeDropdown,
  Heading,
  Hint,
  List,
  NoSearchItemsPlaceholder,
  PaginationBar,
  SearchInput,
  SearchPlaceholderIcon,
  SelectDropdown,
  Text,
  useDebounce,
} from '@common-fe/common-fe';
import _ from 'lodash';

import { ALL_OPTION, ALL_OPTION_KEY, OrganizationTypes } from '@/common/constants';
import Permissions from '@/common/permissions';
import { OptionKey } from '@/common/types';
import { Access } from '@/modules/core/components';
import Topbar from '@/modules/core/components/Topbar/Topbar';
import { useCurrentOrganization } from '@/modules/core/hooks';
import { useListPagination } from '@/modules/employer/hooks';
import { useAuthStore } from '@/modules/user/stores';
import globalTheme from '@/styles/theme';
import { formatDateRange } from '@/utils/modifiers';

import { EmployerStatus } from '../employer/employer.constants';

import useFilesList from './hooks/useFilesList';
import useFilesUploadQuery, { addBacklightingFields, UNKNOWN_VALUE } from './queries/useFilesUploadQuery';
import useOrganizationQuery from './queries/useOrganization.query';
import { FILE_STATUS_OPTIONS, FileDtoWithMediaSource, FileStatus } from './FileManager.constants';
import { useFileDownloadQuery } from './queries';

const AVAILABLE_CONTACT_TYPES = '6,5,3';
const HEADER_HEIGHT = 134;
const HEADER_SPACING = 16;

import { useFilters } from '@/utils';

import FixedTopWrapper from '../core/components/FixedTopWrapper';

import FileListDuplication from './FileListDuplication';
import { StyledAutocompleteDropdown, TableWithBacklighting } from './FilesList.styles';
import FilesListItem from './FilesListItem';
import { useAllowedFileTypes } from './hooks';

const stateAllToggling = (statuses: OptionKey[]) => {
  const isStatusAllSelected = statuses[statuses.length - 1] === ALL_OPTION.value;
  if (statuses.length === 0 || isStatusAllSelected) {
    return [ALL_OPTION.value];
  }
  const isStatusAllChangedToAnother = statuses.length === 2 && statuses[0] === ALL_OPTION.value;
  if (isStatusAllChangedToAnother) {
    return statuses.filter((status) => status !== ALL_OPTION.value);
  }

  return statuses;
};

const SEARCH_APPLY_LEN = 3;

const FilesList = () => {
  const { user } = useAuthStore();
  const {
    observingOrganization: { path },
    observingOrganization,
  } = useCurrentOrganization();

  const [cleaningInitiator, runSearchTextCleaningForAutocomplete] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [hiddenRowsIds, setHiddenRowsIds] = useState<string[]>([]);

  const { page, perPage, setPerPage, setPage } = useListPagination();

  const { filtersValues, setFilterValue, clearAllFilters } = useFilters<{
    searchString: string;
    uploadedById: OptionKey;
    recordTypes: OptionKey[];
    statuses: OptionKey[];
    dateRange: Array<Date | null>;
  }>(
    {
      recordTypes: [ALL_OPTION.value],
      statuses: [ALL_OPTION.value],
      searchString: '',
      uploadedById: '',
      dateRange: [],
    },
    {
      autoClear: true,
    }
  );

  const isTerminatedEmployer = useMemo(
    () =>
      (observingOrganization.type === OrganizationTypes.company ||
        observingOrganization.type === OrganizationTypes.employer) &&
      observingOrganization.status === EmployerStatus.Terminated,
    [observingOrganization]
  );

  const [uploadedFiles, setUploadedFiles] = useState<FileDtoWithMediaSource[]>([]);
  const [newUploadedFile, setNewUploadedFile] = useState<FileDtoWithMediaSource>();
  const { uploadFiles } = useFilesUploadQuery();
  const initUploadingFiles = useCallback(
    async (uploadingFiles: FileDtoWithMediaSource[]) => {
      setUploadedFiles([...uploadingFiles, ...uploadedFiles]);

      const newUploadedFilesOnServer = (await uploadFiles(uploadingFiles)) as FileDtoWithMediaSource[];

      setNewUploadedFile(newUploadedFilesOnServer[0]);
    },
    [uploadFiles, uploadedFiles]
  );
  useEffect(() => {
    if (newUploadedFile) {
      setUploadedFiles([
        ...uploadedFiles.map((file) => {
          if (file.temporalId === newUploadedFile.temporalId) return newUploadedFile;

          return file;
        }),
      ]);
      setNewUploadedFile(undefined);
    }
  }, [newUploadedFile, uploadedFiles]);
  const reuploadingFile = useCallback(
    async (uploadingFile: FileDtoWithMediaSource) => {
      setUploadedFiles(
        uploadedFiles.map((file) => {
          if (file.temporalId === uploadingFile.temporalId) {
            return {
              ...uploadingFile,
              hasReuploading: false,
              type: FileStatus.Uploading,
              status: FileStatus.Uploading,
              ...addBacklightingFields(FileStatus.Uploading),
            };
          }

          return file;
        })
      );

      const reuploadedFiles = (await uploadFiles([uploadingFile])) as FileDtoWithMediaSource[];
      const reuploadedFile = reuploadedFiles[0];

      setUploadedFiles(
        uploadedFiles.map((file) => {
          if (file.temporalId === reuploadedFile.temporalId) {
            return reuploadedFile;
          }

          return file;
        })
      );
    },
    [uploadFiles, setUploadedFiles, uploadedFiles]
  );
  const { getInputProps, open } = useDropzone({
    accept: 'text/csv, text/plain, application/vnd.ms-excel' as unknown as Accept,
    multiple: false,
    onDrop: (acceptedFiles) => {
      initUploadingFiles(
        acceptedFiles.map((file) => {
          const temporalId = `temporalId_${_.uniqueId()}`;
          return {
            file,
            id: temporalId,
            temporalId,
            name: file.name,
            uploadedBy: `${user?.firstName} ${user?.lastName}`.trim(),
            partnerNames: [UNKNOWN_VALUE],
            employerNames: [UNKNOWN_VALUE],
            createdAt: UNKNOWN_VALUE,
            processedAt: UNKNOWN_VALUE,
            status: FileStatus.Uploading,
            ...addBacklightingFields(FileStatus.Uploading),
          };
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }) as any[]
      );
    },
  });
  const { allowedFileTypes, hasOnlyOneFileTypeAllowed, allowedFileRecordTypesOptions } =
    useAllowedFileTypes();
  const statusValues = useMemo(() => stateAllToggling(filtersValues.statuses), [filtersValues.statuses]);
  const recordTypesValues = useMemo(
    () => stateAllToggling(filtersValues.recordTypes),
    [filtersValues.recordTypes]
  );

  const currentTypes = useMemo(() => {
    return recordTypesValues.filter((item) => item !== ALL_OPTION_KEY);
  }, [recordTypesValues]);

  const hasStatus = useMemo(
    () => statusValues.length > 0 && !statusValues.some((status) => status === ALL_OPTION.value),
    [statusValues]
  );
  const hasRecordTypes = useMemo(
    () => recordTypesValues.length > 0 && !recordTypesValues.some((status) => status === ALL_OPTION.value),
    [recordTypesValues]
  );

  const hasDateRange = useMemo(
    () => Boolean(filtersValues.dateRange[0] && filtersValues.dateRange[1]),
    [filtersValues.dateRange]
  );
  const areFiltersUsed = useMemo(
    () =>
      Boolean(
        filtersValues.searchString ||
        filtersValues.uploadedById ||
        hasRecordTypes ||
        hasStatus ||
        hasDateRange
      ),
    [filtersValues.searchString, filtersValues.uploadedById, hasRecordTypes, hasStatus, hasDateRange]
  );
  const hasClearAll = useMemo(
    () =>
      Boolean(
        filtersValues.searchString ||
        filtersValues.uploadedById ||
        hasRecordTypes ||
        hasStatus ||
        hasDateRange
      ),
    [filtersValues.searchString, filtersValues.uploadedById, hasRecordTypes, hasStatus, hasDateRange]
  );

  const [sendingToProcessFileId, setSendingToProcessFileId] = useState('');

  const debouncedSearch = useDebounce(filtersValues.searchString);
  const { total, isLoading, updatedFiles, onFileProcess } = useFilesList({
    onRemoveRow: (id: string) => setHiddenRowsIds([...hiddenRowsIds, id]),
    uploadedFiles: uploadedFiles.filter(
      (uploadedFile) => !hiddenRowsIds.some((id) => id === uploadedFile.temporalId)
    ),
    page: page - 1,
    perPage,
    ...(debouncedSearch && debouncedSearch.length >= SEARCH_APPLY_LEN
      ? { searchString: debouncedSearch }
      : {}),
    ...(hasStatus ? { statuses: statusValues } : {}),
    ...(hasRecordTypes ? { types: currentTypes } : {}),
    ...(hasDateRange ? { dateRange: formatDateRange(filtersValues.dateRange) } : {}),
    ...(filtersValues.uploadedById ? { uploadedById: filtersValues.uploadedById } : {}),
    reuploadingFile,
    sendingToProcessFileId,
  });

  const handleProcess = useCallback(
    async (id: string) => {
      setSendingToProcessFileId(id);
      await onFileProcess({ id } as FileDtoWithMediaSource);
      setSendingToProcessFileId('');
    },
    [onFileProcess]
  );

  const totalFiles = useMemo(
    () => uploadedFiles.filter((file) => file.temporalId !== file.id).length + total - hiddenRowsIds.length,
    [uploadedFiles, total, hiddenRowsIds]
  );

  useEffect(() => {
    if (allowedFileTypes && hasOnlyOneFileTypeAllowed) {
      setFilterValue.recordTypes([capitalizeFirstLetter(allowedFileTypes[0])]);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isLoading && uploadedFiles.length) {
      setUploadedFiles([]);
      setHiddenRowsIds([]);
    }
  }, [isLoading, uploadedFiles, setUploadedFiles]);

  useEffect(() => {
    const appContainer = document.getElementById(CONTENT_ID_SELECTOR);
    const updateScrolling = () =>
      setIsScrolled(!!appContainer && appContainer.scrollTop > HEADER_SPACING / 2);
    appContainer?.addEventListener('scroll', updateScrolling);
    return () => {
      setIsScrolled(false);
      appContainer?.removeEventListener('scroll', updateScrolling);
    };
  }, []);
  const handleDownloadFileById = useFileDownloadQuery();
  const [fileSearchText, setFileSearchText] = useState('');
  const { data, isLoading: isLoadingSearch } = useOrganizationQuery(fileSearchText, {
    observingOrganizationPath: path,
    type: AVAILABLE_CONTACT_TYPES,
  });
  const handlePickFieldSearchOption = useCallback(
    (id: OptionKey) => {
      setPage(1);
      setFilterValue.uploadedById(id);
    },
    [setFilterValue, setPage]
  );
  const handlePickRecord = useCallback(
    (type: OptionKey[]) => {
      setFilterValue.recordTypes(type);
      setPage(1);
    },
    [setFilterValue, setPage]
  );

  const handleClearProcessedFile = useCallback(
    (fileId: string) => {
      const cleanList = uploadedFiles.filter((item) => item.id !== fileId);
      setUploadedFiles([...cleanList]);
    },
    [uploadedFiles]
  );

  const handleClear = useCallback(() => {
    clearAllFilters();
    runSearchTextCleaningForAutocomplete(!cleaningInitiator);
  }, [cleaningInitiator, clearAllFilters]);

  return (
    <>
      <FixedTopWrapper>
        <Topbar hideHeader={isScrolled} />
        <Box
          direction="row"
          justify="between"
          alignSelf="center"
          wrap
          width={globalTheme.defaultContentWidth}
          margin={{ top: 'medium' }}
        >
          <Box margin={{ bottom: 'spacing16' }}>
            <Heading level={1} size="regular" color="textTitle">
              File Manager
            </Heading>
          </Box>
          <Access
            accessRequirements={[
              {
                permission: Permissions.PROCESS_FILE,
              },
            ]}
          >
            <Box
              flex
              direction="row"
              justify="end"
              align="center"
              height="40px"
              margin={{ bottom: isScrolled ? 'spacing4' : 'spacing16' }}
            >
              <input {...getInputProps()} />
              <Hint
                hintAlign="left"
                hintWidth={220}
                hideHint={!isTerminatedEmployer}
                hintElement={
                  <AppButton
                    testId="FileManager-uploadFile"
                    onClick={open}
                    width="140px"
                    disabled={isTerminatedEmployer}
                  >
                    Upload a File
                  </AppButton>
                }
                marginContent="m"
              >
                <Box direction="row" justify="center" align="center">
                  <Text textAlign="center">Terminated organizations cannot upload files.</Text>
                </Box>
              </Hint>
            </Box>
          </Access>
        </Box>
      </FixedTopWrapper>

      <Box
        flex
        direction="column"
        margin={{ top: `${HEADER_HEIGHT + HEADER_SPACING}px` }}
        width={globalTheme.defaultContentWidth}
      >
        <ClearAll onClick={handleClear} isVisible={hasClearAll && !isScrolled} />

        <TableWithBacklighting background={{ color: 'module' }} round="container1Round">
          <Box align="center" pad={{ bottom: 'spacing16' }}>
            <Box
              width="100%"
              direction="row"
              justify="between"
              align="center"
              pad={{ horizontal: 'spacing24', top: 'spacing24' }}
            >
              <Text weight="bold" color="textBody" style={{ whiteSpace: 'nowrap' }}>
                Files: {totalFiles || 0}
              </Text>
              <Box direction="row" justify="end">
                <Box margin={{ left: 'spacing12' }} width={{ min: '220px' }}>
                  <SearchInput
                    className="search-input"
                    testId="FileManager_search"
                    hasSearchingHistory
                    value={filtersValues.searchString}
                    placeholder="File, Partner, Employer"
                    onChange={setFilterValue.searchString}
                  />
                </Box>
                <Box margin={{ left: 'spacing12' }} width={{ min: 'control', max: '180px' }}>
                  <StyledAutocompleteDropdown
                    name="uploadedBy"
                    testId="FileManager_uploaded-by"
                    placeholder="Uploaded By"
                    completeValue={filtersValues.uploadedById as string}
                    onSearch={setFileSearchText}
                    options={data}
                    isLoading={isLoadingSearch}
                    defaultValue={filtersValues.uploadedById as string}
                    onChange={handlePickFieldSearchOption}
                    value={filtersValues.uploadedById}
                    cleaningInitiator={cleaningInitiator}
                    // isSearchMode
                    className="ellipsis"
                  />
                </Box>
                <Box margin={{ left: 'spacing12' }} width={{ min: '170px', max: '170px' }}>
                  <SelectDropdown
                    id="type"
                    allMode
                    activeTitle
                    ellipsisMode
                    testId="FileManager_record-type"
                    name="Record type"
                    prefix="Record type"
                    options={allowedFileRecordTypesOptions}
                    singleMode={allowedFileRecordTypesOptions.length === 1}
                    defaultValue={recordTypesValues as string[]}
                    values={recordTypesValues}
                    onChangeValues={handlePickRecord}
                  />
                </Box>
                <Box margin={{ left: 'spacing12' }} width={{ min: '150px', max: '150px' }}>
                  <SelectDropdown
                    id="status"
                    allMode
                    activeTitle
                    testId="FileManager_activity-status"
                    ellipsisMode
                    name="Activity status"
                    prefix="Status"
                    options={FILE_STATUS_OPTIONS}
                    values={statusValues}
                    dropdownStyles={{
                      minWidth: '200px',
                    }}
                    onChangeValues={(status: OptionKey[]) => {
                      setFilterValue.statuses(status);
                      setPage(1);
                    }}
                  />
                </Box>
                <Box margin={{ left: 'spacing12' }} width={{ min: 'control' }}>
                  <DateRangeDropdown
                    prefix="Received:"
                    testId="FileManager_received-period"
                    className="period-picker"
                    onChange={(range) => {
                      setFilterValue.dateRange(range);
                      setPage(1);
                    }}
                    ellipsisMode
                    startDate={filtersValues.dateRange[0]}
                    endDate={filtersValues.dateRange[1]}
                    icon={<CalendarIcon color="iconPrimary" />}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
          <Box pad={{ top: '0', bottom: 'spacing24', horizontal: 'spacing24' }}>
            <FileListDuplication />
            <List>
              <List.Header>
                <List.Header.Item flex={1.15}>Record type / Name</List.Header.Item> <Box width="30px" />
                <List.Header.Item flex={0.85}>Partner</List.Header.Item>
                <List.Header.Item flex={0.9}>Employer</List.Header.Item>
                <List.Header.Item flex={0.85}>Uploaded by</List.Header.Item>
                <List.Header.Item flex={1.2}>Date Received / Processed</List.Header.Item>
                <List.Header.Item flex={0.45}></List.Header.Item>
                <List.Header.Item flex={0.8}>
                  <Box fill direction="row-reverse">
                    <Text size="small">Status</Text>
                  </Box>
                </List.Header.Item>
                <Box width="24px"></Box>
              </List.Header>

              {isLoading && <List.Preloader count={4} />}
              {totalFiles === 0 && !updatedFiles.length && !isLoading && (
                <NoSearchItemsPlaceholder
                  data-testId="FileManager_placeholder"
                  placeholderTitle={areFiltersUsed ? 'No results have been found' : 'There are no files yet'}
                  node={
                    areFiltersUsed ? (
                      <SearchPlaceholderIcon
                        data-testId="FileManager_placeholder_filter-used-icon"
                        size="xxlarge"
                        color="iconSecondary"
                      />
                    ) : (
                      <AbsenceFilesPlaceholderIcon
                        data-testId="FileManager_placeholder_filter-unused-icon"
                        size="xxlarge"
                        color="iconSecondary"
                      />
                    )
                  }
                  description={
                    areFiltersUsed
                      ? "Try to adjust your search or filter to find what you're looking for."
                      : ''
                  }
                />
              )}
              <Box key={`${isLoading}`}>
                {updatedFiles.map((item) => (
                  <FilesListItem
                    key={item.id || item.temporalId}
                    {...item}
                    sendingToProcessFileId={sendingToProcessFileId}
                    onProcessFile={() => {
                      handleProcess(item.id || item.temporalId || '');
                    }}
                    onCleanProcessedFile={handleClearProcessedFile}
                    onReuploading={() => {
                      reuploadingFile(item);
                    }}
                    onDownload={() => {
                      handleDownloadFileById(item.id || item.temporalId || '');
                    }}
                  />
                ))}
              </Box>
            </List>
            <PaginationBar
              page={page}
              total={totalFiles}
              pageSize={perPage}
              onChangePage={setPage}
              onChangePageSize={setPerPage}
            />
          </Box>
        </TableWithBacklighting>
      </Box>
    </>
  );
};

export default FilesList;
